import { useCallback, useEffect, useMemo, useState } from 'react'
import { useMedplum } from '@medplum/react'
import { useQueryClient } from '@tanstack/react-query'
import type { Organization } from '@medplum/fhirtypes'
import { notifications } from '@mantine/notifications'
import { useMiddlewareClient } from '@/hooks/useMiddlewareClient'
import { patientChartCountsQueryKey } from '@/hooks/patientChartCountsQueryKey'
import { pdfBytesToBase64, downloadPdfBlob } from '../pdf/pdfHelpers'
import type { DiagnosisDefault } from './types'
import { getErrorMessage, logError } from './errorUtils'
import { formatProviderName } from '@/utils/providerUtils'

interface UseOrderSubmissionOptions {
  patientId: string | undefined
  questionnaireId: string
  generatePdfBytes: () => Promise<Uint8Array>
  buildFileName: () => string
  /** The complete form data object sent to the backend for audit trail. */
  getFormData: () => Record<string, unknown>
  vendorName: string
  laboratoryOrganizationName: string
  /** Name of the destination facility the order is sent to. Used as the fallback
   *  "doctor" on written-back diagnoses when the ordering practitioner has no name.
   *  Defaults to the lab organization name when not provided (lab vendors). */
  facilityName?: string
  /** Original diagnosis codes from patient chart at load time. */
  diagnosisDefaults?: DiagnosisDefault[]
  /** Returns the current diagnosisText from the form at submit time. */
  getDiagnosisText?: () => string
  /** Blocking error from organization lookup — prevents submission. */
  orgError?: string | null
  /** If provided, skips the lab Organization fax lookup and uses this fax number directly.
   *  Used for radiology where the fax comes from the selected facility, not an Organization resource.
   *  `null` = override mode active but no facility selected yet (no error shown).
   *  `''`   = facility selected but has no fax (error shown).
   *  string = facility fax number. */
  overrideFaxNumber?: string | null
  /** When true, this order is not faxed anywhere (e.g. patient-carried referral).
   *  Skips the lab fax lookup (no blocking) and the /order/send-fax call. /order/create still runs. */
  skipFax?: boolean
}

const CANONICAL_LAB_ORGANIZATION_NAMES = {
  INVITAE: 'Invitae',
  ACCU_REFERENCE: 'AccuReference',
  LABCORP: 'Labcorp',
  EXACT_SCIENCES: 'Exact Sciences (Cologuard)',
  QUEST: 'Quest Diagnostics',
} as const

const LAB_ORGANIZATION_NAME_MAP: Record<string, string> = {
  Invitae: CANONICAL_LAB_ORGANIZATION_NAMES.INVITAE,
  'Invitae Cancer': CANONICAL_LAB_ORGANIZATION_NAMES.INVITAE,
  'Invitae Cardiology': CANONICAL_LAB_ORGANIZATION_NAMES.INVITAE,
  'Accu Reference': CANONICAL_LAB_ORGANIZATION_NAMES.ACCU_REFERENCE,
  AccuReference: CANONICAL_LAB_ORGANIZATION_NAMES.ACCU_REFERENCE,
  Labcorp: CANONICAL_LAB_ORGANIZATION_NAMES.LABCORP,
  Cologuard: CANONICAL_LAB_ORGANIZATION_NAMES.EXACT_SCIENCES,
  'Exact Sciences': CANONICAL_LAB_ORGANIZATION_NAMES.EXACT_SCIENCES,
  'Exact Sciences (Cologuard)': CANONICAL_LAB_ORGANIZATION_NAMES.EXACT_SCIENCES,
  Quest: CANONICAL_LAB_ORGANIZATION_NAMES.QUEST,
  'Quest Diagnostics': CANONICAL_LAB_ORGANIZATION_NAMES.QUEST,
}

/**
 * Hook that encapsulates PDF generation, order submission, and preview/download.
 * Shared across all vendor forms.
 */
export function useOrderSubmission(options: UseOrderSubmissionOptions) {
  const {
    patientId,
    questionnaireId,
    generatePdfBytes,
    buildFileName,
    getFormData,
    vendorName,
    laboratoryOrganizationName,
    facilityName,
    diagnosisDefaults,
    getDiagnosisText,
    orgError,
  } = options
  const medplum = useMedplum()
  const middleware = useMiddlewareClient()
  const queryClient = useQueryClient()
  const [exporting, setExporting] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [orderSubmitted, setOrderSubmitted] = useState(false)
  const [faxNumber, setFaxNumber] = useState<string>('')
  const [labReady, setLabReady] = useState(false)
  const [labError, setLabError] = useState<string | null>(null)

  const canonicalLaboratoryOrganizationName = useMemo(() => {
    const normalizedInput = laboratoryOrganizationName.trim()
    return LAB_ORGANIZATION_NAME_MAP[normalizedInput] || normalizedInput
  }, [laboratoryOrganizationName])

  const buildPdfBlob = (bytes: Uint8Array) => {
    const arrayBuffer = Uint8Array.from(bytes).buffer
    return new Blob([arrayBuffer], { type: 'application/pdf' })
  }

  const getLaboratoryFaxNumber = useCallback(async (): Promise<string> => {
    const exactMatch = await medplum.searchOne(
      'Organization',
      `type=laboratory&name:exact=${encodeURIComponent(canonicalLaboratoryOrganizationName)}`,
    )

    if (exactMatch) {
      return exactMatch.telecom?.find((item) => item.system === 'fax')?.value?.trim() || ''
    }

    const result = await medplum.search(
      'Organization',
      `_count=20&type=laboratory&name=${encodeURIComponent(canonicalLaboratoryOrganizationName)}`,
    )

    const organizations = result.entry?.map((entry) => entry.resource as Organization) || []
    const exactOrganization = organizations.find(
      (org) => org.name?.toLowerCase() === canonicalLaboratoryOrganizationName.toLowerCase(),
    )
    const matchedOrganization =
      exactOrganization ||
      organizations.find((org) =>
        org.name?.toLowerCase().includes(canonicalLaboratoryOrganizationName.toLowerCase()),
      )

    return matchedOrganization?.telecom?.find((item) => item.system === 'fax')?.value?.trim() || ''
  }, [canonicalLaboratoryOrganizationName, medplum])

  useEffect(() => {
    if (options.skipFax) {
      setFaxNumber('')
      setLabReady(true)
      setLabError(null)
      return
    }

    // When overrideFaxNumber is provided (radiology), skip the Organization lookup entirely.
    // A value of `undefined` means "use the Organization lookup" (lab forms).
    // A value of `null` means "override mode, but no facility selected yet" — no error, just not ready.
    // A non-empty string means the facility fax is known.
    // An empty string means facility selected but has no fax.
    if (options.overrideFaxNumber !== undefined) {
      if (options.overrideFaxNumber === null) {
        // No facility selected yet — don't show an error
        setFaxNumber('')
        setLabReady(false)
        setLabError(null)
      } else if (options.overrideFaxNumber) {
        setFaxNumber(options.overrideFaxNumber)
        setLabReady(true)
        setLabError(null)
      } else {
        setFaxNumber('')
        setLabReady(false)
        setLabError('No fax number available for the selected facility.')
      }
      return
    }

    let mounted = true
    getLaboratoryFaxNumber()
      .then((value) => {
        if (!mounted) return
        setFaxNumber(value)
        if (value) {
          setLabReady(true)
          setLabError(null)
        } else {
          setLabReady(false)
          setLabError(
            `Unable to locate fax number for ${canonicalLaboratoryOrganizationName}. Order cannot be submitted.`,
          )
        }
      })
      .catch((err) => {
        logError(`Lab fax lookup for ${canonicalLaboratoryOrganizationName}`, err)
        if (!mounted) return
        setFaxNumber('')
        setLabReady(false)
        setLabError(
          `Unable to locate fax number for ${canonicalLaboratoryOrganizationName}. Order cannot be submitted.`,
        )
      })

    return () => {
      mounted = false
    }
  }, [
    options.skipFax,
    options.overrideFaxNumber,
    getLaboratoryFaxNumber,
    canonicalLaboratoryOrganizationName,
  ])

  const handlePreviewPdf = async () => {
    setExporting(true)
    try {
      const pdfBytes = await generatePdfBytes()
      const blob = buildPdfBlob(pdfBytes)
      downloadPdfBlob(blob, buildFileName())
      notifications.show({
        title: 'PDF Downloaded',
        message: `The ${vendorName} order requisition has been downloaded.`,
        color: 'blue',
      })
    } catch (err) {
      logError('PDF export', err)
      notifications.show({
        title: 'PDF Export Failed',
        message: getErrorMessage(err),
        color: 'red',
      })
    } finally {
      setExporting(false)
    }
  }

  const handleSubmitOrder = async () => {
    if (orgError) {
      notifications.show({
        title: 'Organization Error',
        message: orgError,
        color: 'red',
      })
      return
    }

    setSubmitting(true)
    try {
      // Resolve fax number before creating the order
      let resolvedFaxNumber = ''
      if (!options.skipFax) {
        resolvedFaxNumber = faxNumber
        if (!resolvedFaxNumber && options.overrideFaxNumber === undefined) {
          resolvedFaxNumber = await getLaboratoryFaxNumber()
        }
        if (!resolvedFaxNumber) {
          notifications.show({
            title: 'Order Submission Blocked',
            message: `No fax number found for ${canonicalLaboratoryOrganizationName}. Please contact support.`,
            color: 'red',
          })
          return
        }
      }

      const pdfBytes = await generatePdfBytes()
      const pdfBase64 = pdfBytesToBase64(pdfBytes)
      const pdfFileName = buildFileName()

      if (!patientId) {
        throw new Error('Patient record is missing an ID. Please refresh the page.')
      }

      const profile = medplum.getProfile()
      const practitionerId = profile?.id
      if (!practitionerId) {
        throw new Error('Unable to determine practitioner. Please log in again.')
      }

      // "Doctor" shown on the diagnosis: the ordering practitioner's name with title
      // (e.g. "Jane Smith, MD"), falling back to the destination facility name when the
      // practitioner has no name on file. The Diagnoses table renders Condition.asserter.display.
      const providerDisplay = formatProviderName(profile?.name?.[0])
      const diagnosisAttribution =
        providerDisplay || facilityName || canonicalLaboratoryOrganizationName

      // Save newly added diagnosis codes BEFORE creating the order — all must succeed
      if (diagnosisDefaults && getDiagnosisText) {
        const originalCodes = new Set(diagnosisDefaults.map((d) => d.code.toUpperCase()))
        const currentLines = getDiagnosisText().split('\n').filter(Boolean)
        const newCodes = currentLines
          .map((line) => {
            const code = line.split(/\s/)[0]?.toUpperCase()
            const display = code ? line.slice(code.length).trim() : ''
            return { code, display }
          })
          .filter(({ code }) => code && !originalCodes.has(code))

        for (const { code, display } of newCodes) {
          try {
            await medplum.createResource({
              resourceType: 'Condition',
              subject: { reference: `Patient/${patientId}` },
              code: {
                coding: [
                  {
                    system: 'http://hl7.org/fhir/sid/icd-10-cm',
                    code,
                    display: display || code,
                  },
                ],
              },
              clinicalStatus: {
                coding: [
                  {
                    system: 'http://terminology.hl7.org/CodeSystem/condition-clinical',
                    code: 'active',
                    display: 'Active',
                  },
                ],
              },
              category: [
                {
                  coding: [
                    {
                      system: 'http://terminology.hl7.org/CodeSystem/condition-category',
                      code: 'problem-list-item',
                      display: 'Problem List Item',
                    },
                  ],
                },
              ],
              recorder: {
                reference: `Practitioner/${practitionerId}`,
                display: diagnosisAttribution,
              },
              asserter: {
                reference: `Practitioner/${practitionerId}`,
                display: diagnosisAttribution,
              },
            })
          } catch (diagErr) {
            logError(`Saving diagnosis code ${code}`, diagErr)
            notifications.show({
              title: 'Failed to Save Diagnosis Code',
              message: `Could not save diagnosis code ${code} to the patient chart: ${getErrorMessage(diagErr)}. The order was not created.`,
              color: 'red',
            })
            return
          }
        }
      }

      let requestGroupId: string | undefined
      try {
        const created = await middleware.post('/order/create', {
          patientId,
          practitionerId,
          questionnaireId,
          formData: getFormData(),
          pdfBase64,
          pdfFileName,
        })
        requestGroupId = created?.requestGroupId
      } catch (orderErr) {
        logError('Order creation', orderErr)
        notifications.show({
          title: 'Failed to Create Order',
          message: `Could not create the ${vendorName} order: ${getErrorMessage(orderErr)}. No fax was sent.`,
          color: 'red',
        })
        return
      }

      if (!options.skipFax) {
        try {
          await middleware.post('/order/send-fax', {
            faxNumber: resolvedFaxNumber,
            pdfBase64,
            pdfFileName,
            // PD-1651 — the server stamps the RingCentral message id on the order so the
            // Orders view can show whether the fax was delivered, and records a specialist
            // referral's recipient (Frequently used, saved fax) once the fax is accepted.
            ...(requestGroupId ? { requestGroupId } : {}),
          })
        } catch (faxErr) {
          logError('Fax send', faxErr)
          notifications.show({
            title: 'Fax Sending Failed',
            message: `The order was created, but the fax to ${canonicalLaboratoryOrganizationName} failed: ${getErrorMessage(faxErr)}. Contact support to resend.`,
            color: 'yellow',
          })
        }
      }

      setOrderSubmitted(true)
      notifications.show({
        title: 'Order Submitted',
        message: `${vendorName} order created successfully.`,
        color: 'green',
      })

      // Refresh the patient chart counts so the Orders badge updates without a page reload
      if (patientId) {
        void queryClient.invalidateQueries({ queryKey: patientChartCountsQueryKey(patientId) })
      }
    } catch (err) {
      logError('Order submission', err)
      notifications.show({
        title: 'Order Submission Failed',
        message: getErrorMessage(err),
        color: 'red',
      })
    } finally {
      setSubmitting(false)
    }
  }

  const handleSaveDraft = useCallback(async (): Promise<boolean> => {
    setSubmitting(true)
    try {
      const pdfBytes = await generatePdfBytes()
      const pdfBase64 = pdfBytesToBase64(pdfBytes)
      const pdfFileName = buildFileName()

      if (!patientId) {
        throw new Error('Patient record is missing an ID. Please refresh the page.')
      }

      const profile = medplum.getProfile()
      const practitionerId = profile?.id
      if (!practitionerId) {
        throw new Error('Unable to determine practitioner. Please log in again.')
      }

      const formDataWithDraft = { ...getFormData(), orderStatus: 'draft' }

      try {
        await middleware.post('/order/create', {
          patientId,
          practitionerId,
          questionnaireId,
          formData: formDataWithDraft,
          pdfBase64,
          pdfFileName,
        })
      } catch (orderErr) {
        logError('Draft order creation', orderErr)
        notifications.show({
          title: 'Failed to Save Draft',
          message: `Could not save the ${vendorName} draft order: ${getErrorMessage(orderErr)}`,
          color: 'red',
        })
        return false
      }

      setOrderSubmitted(true)
      notifications.show({
        title: 'Draft saved',
        message: 'This order has not been submitted and is not visible to the patient.',
        color: 'blue',
      })

      // Refresh the patient chart counts so the Orders badge updates without a page reload
      if (patientId) {
        void queryClient.invalidateQueries({ queryKey: patientChartCountsQueryKey(patientId) })
      }

      return true
    } catch (err) {
      logError('Draft save', err)
      notifications.show({
        title: 'Draft Save Failed',
        message: getErrorMessage(err),
        color: 'red',
      })
      return false
    } finally {
      setSubmitting(false)
    }
  }, [
    buildFileName,
    generatePdfBytes,
    getFormData,
    medplum,
    middleware,
    patientId,
    queryClient,
    questionnaireId,
    vendorName,
  ])

  return {
    exporting,
    submitting,
    orderSubmitted,
    labReady,
    labError,
    handlePreviewPdf,
    handleSubmitOrder,
    handleSaveDraft,
  }
}
